import { api } from "api";

/**
 * The restaurant wallet (api#147, admin#41).
 *
 * `userId` IS THE RESTAURANT'S users.id — the `id` every vendor row in this
 * dashboard carries, NOT `vendorId`. The API translates it once. See AGENTS.md,
 * "The id trap".
 *
 * @typedef {object} VendorWalletSummary
 * @property {boolean} enabled This restaurant is on the wallet.
 * @property {boolean} city_enabled The city master switch.
 * @property {boolean} effective Orders cost this restaurant a commission now.
 * @property {number} balance Signed, in ILS. A debt is negative.
 * @property {boolean} blocked The wallet closed the restaurant.
 * @property {string|null} blocked_at
 * @property {string|null} blocked_reason The Arabic text to show, or null.
 *
 * @typedef {object} VendorWalletMovement
 * @property {number} id
 * @property {"topup"|"order_charge"|"order_refund"|"adjustment"} type
 * @property {number|string} amount Signed.
 * @property {number|string} balance_after
 * @property {number|null} orderId
 * @property {number|string|null} rate
 * @property {string|null} note
 * @property {{id: number, name: string}|null} actor
 * @property {string} createdAt
 *
 * @typedef {object} VendorWalletPage
 * @property {{userId: number, vendorId: number, name: string, status: string}} vendor
 * @property {VendorWalletSummary} wallet
 * @property {number} count
 * @property {number} pages
 * @property {VendorWalletMovement[]} results
 */

/**
 * One restaurant's balance and its ledger. The balance is the server's ledger
 * SUM, not a cached column, so this is the screen to open when a figure looks
 * wrong.
 *
 * @param {number|string} userId
 * @param {{page?: number, size?: number}} [params]
 * @returns {Promise<VendorWalletPage>}
 */
export const getVendorWallet = async (userId, params = {}) => {
    const response = await api.get(`vendor/${userId}/wallet`, { params });
    return response.data;
};

/**
 * Credit a restaurant. Positive amounts only — the server refuses the rest.
 *
 * `idempotencyKey` travels in the body, not in a header: the API reads either,
 * and a body field needs no CORS change. Send the SAME key when you retry the
 * same top-up, and the server credits it once.
 *
 * @param {number|string} userId
 * @param {{amount: number, note?: string, idempotencyKey: string}} payload
 */
export const topUpVendorWallet = async (userId, { amount, note, idempotencyKey }) => {
    const response = await api.post(`vendor/${userId}/wallet/topup`, {
        amount,
        note,
        idempotencyKey,
    });
    return response.data;
};

/**
 * Take money from a restaurant's wallet (api#147). `amount` is positive; the
 * server writes it as a negative adjustment. `note` is required: the
 * restaurant reads it. At ₪0 or less, a restaurant on the wallet closes.
 *
 * @param {number|string} userId
 * @param {{amount: number, note: string, idempotencyKey: string}} payload
 */
export const deductVendorWallet = async (userId, { amount, note, idempotencyKey }) => {
    const response = await api.post(`vendor/${userId}/wallet/deduct`, {
        amount,
        note,
        idempotencyKey,
    });
    return response.data;
};

/**
 * Put one restaurant on the wallet, or take it off.
 *
 * @param {number|string} userId
 * @param {boolean} enabled
 */
export const updateVendorWalletSettings = async (userId, enabled) => {
    const response = await api.patch(`vendor/${userId}/wallet/settings`, { enabled });
    return response.data;
};

/**
 * @typedef {object} CityVendorWalletPolicy
 * @property {number} id
 * @property {boolean} vendor_wallet_enabled
 * @property {number} vendor_commission_default_percentage
 * @property {number} vendorCount Active restaurants in the city.
 * @property {number} walletVendorCount How many of them are on the wallet.
 * @property {number} emptyWalletCount How many of those hold ₪0 or less.
 * @property {number} blockedCount How many the wallet keeps closed now.
 */

/**
 * @param {number|string} cityId
 * @returns {Promise<CityVendorWalletPolicy>}
 */
export const getCityVendorWallet = async (cityId) => {
    const response = await api.get(`api/city/${cityId}/vendor-wallet`);
    return response.data;
};

/**
 * The switch and the restaurant default rate, saved together. Either may be
 * left out; the server moves the wallet blocks only when the switch changes.
 *
 * @param {{cityId: number|string, enabled?: boolean, vendorPercentage?: number}} payload
 * @returns {Promise<CityVendorWalletPolicy>}
 */
export const updateCityVendorWallet = async ({ cityId, enabled, vendorPercentage }) => {
    const response = await api.patch(`api/city/${cityId}/vendor-wallet`, {
        enabled,
        vendorPercentage,
    });
    return response.data;
};
